import { useEffect, useState } from "react";
import {
  DEMOCRACY_VOTE_MODES,
  isPopcorn,
  type DemocracyVoteMode,
  type GameDto,
  type PlayerDto,
  type Side,
  type TimeControl,
  type UserDto,
} from "@chess/shared";

import { AddBotControl } from "./AddBotControl.js";
import { TeamOrderList } from "./TeamOrderList.js";
import { ApiError, joinGame, removeBot, reorderTeam, updateDemocracyLobbySettings } from "./api.js";
import { describeTimeControl, TimeControlPicker } from "./TimeControlPicker.js";

function timeControlFor(game: GameDto): TimeControl | null {
  if (!game.clock) return null;
  return {
    initialSeconds: game.clock.initialSeconds,
    incrementSeconds: game.clock.incrementSeconds,
  };
}

function sameTimeControl(left: TimeControl | null, right: TimeControl | null): boolean {
  return (
    left === right ||
    (left !== null &&
      right !== null &&
      left.initialSeconds === right.initialSeconds &&
      left.incrementSeconds === right.incrementSeconds)
  );
}

function Team({
  gameId,
  side,
  players,
  me,
  online,
  onRemoveBot,
  popcorn,
  onReorder,
}: {
  gameId: string;
  side: Side;
  players: PlayerDto[];
  me: PlayerDto | undefined;
  online: string[];
  onRemoveBot: (botUserId: string) => void;
  popcorn: boolean;
  onReorder: (userIds: string[]) => void;
}) {
  return (
    <section className={`team team--${side}`}>
      <header>
        <span className={`piece-dot piece-dot--${side}`} aria-hidden />{" "}
        <h3>{side === "white" ? "White" : "Black"} team</h3>
        <span className="tag">{players.length}/10</span>
      </header>

      {popcorn && players.length > 0 ? (
        <>
          <p className="footnote muted">
            Drag to set the order teammates move in, or use the arrows.
          </p>
          <TeamOrderList players={players} meId={me?.userId ?? ""} onReorder={onReorder} />
          {players.some((player) => !player.ready) ? (
            <p className="footnote muted">
              {players.filter((player) => player.ready).length}/{players.length} ready
            </p>
          ) : null}
        </>
      ) : players.length === 0 ? (
        <p className="muted">Open team</p>
      ) : (
        <ul className="team__players">
          {players.map((player) => (
            <li key={player.userId}>
              <span>
                {player.isBot ? (
                  <span className="tag tag--bot" title="Bot">
                    BOT
                  </span>
                ) : null}
                {player.username}
                {player.userId === me?.userId ? <span className="tag tag--quiet">you</span> : null}
              </span>
              <span className={player.ready ? "team__ready" : "muted"}>
                {player.ready ? "Ready" : "Not ready"}
              </span>
              {player.isBot ? (
                <button
                  type="button"
                  className="button--secondary button--compact"
                  aria-label={`Remove ${player.username}`}
                  onClick={() => onRemoveBot(player.userId)}
                >
                  Remove
                </button>
              ) : online.includes(player.userId) ? (
                <span className="dot dot--on" aria-label="Online" />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <AddBotControl gameId={gameId} side={side} disabled={players.length >= 10} />
    </section>
  );
}

export function DemocracyLobby({
  game,
  user,
  online,
  ready,
}: {
  game: GameDto;
  user: UserDto;
  online: string[];
  ready: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [timeControl, setTimeControl] = useState<TimeControl | null>(() => timeControlFor(game));
  const [voteMode, setVoteMode] = useState<DemocracyVoteMode>(game.democracyVoteMode ?? "random");
  const [savingSettings, setSavingSettings] = useState(false);
  const [copied, setCopied] = useState(false);
  const me = game.players.find((player) => player.userId === user.id);
  const white = game.players.filter((player) => player.side === "white");
  const black = game.players.filter((player) => player.side === "black");
  const allReady = game.democracy?.readyCount === game.players.length;
  const canStart = white.length > 0 && black.length > 0 && allReady;
  const voteRule = DEMOCRACY_VOTE_MODES.find((entry) => entry.mode === game.democracyVoteMode);
  const isCreator = game.createdBy === user.id;
  const savedTimeControl = timeControlFor(game);
  const settingsChanged =
    !sameTimeControl(timeControl, savedTimeControl) || voteMode !== (game.democracyVoteMode ?? "random");

  useEffect(() => {
    setTimeControl(timeControlFor(game));
    setVoteMode(game.democracyVoteMode ?? "random");
  }, [game.clock?.initialSeconds, game.clock?.incrementSeconds, game.democracyVoteMode]);

  async function choose(side: Side) {
    try {
      await joinGame(game.id, side);
      setError(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't join that team.");
    }
  }

  async function handleRemoveBot(botUserId: string) {
    try {
      await removeBot(game.id, botUserId);
      setError(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't remove that bot.");
    }
  }

  async function handleReorder(side: Side, userIds: string[]) {
    try {
      await reorderTeam(game.id, side, userIds);
      setError(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't save that order.");
    }
  }

  async function saveSettings() {
    setSavingSettings(true);
    try {
      await updateDemocracyLobbySettings(game.id, timeControl, voteMode);
      setError(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Couldn't save the lobby settings.");
    } finally {
      setSavingSettings(false);
    }
  }

  async function shareLobby() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
      setError(null);
    } catch {
      setError("Couldn't copy the lobby link.");
    }
  }

  return (
    <div className="democracy-lobby">
      <section className="card democracy-lobby__intro">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Team chess</p>
            <h2>{game.lobbyName}</h2>
          </div>
          <button type="button" className="button--secondary" onClick={() => void shareLobby()}>
            {copied ? "Link copied" : "Share link"}
          </button>
        </div>
        <p className="muted">Choose a side freely until every player votes to start. Roster or settings changes reopen every ready vote.</p>
        <p className="footnote muted"><strong>{voteRule?.name ?? "Lottery mode"} · {describeTimeControl(savedTimeControl)}:</strong> {voteRule?.blurb}</p>
        <div className="team-picker">
          <button type="button" className={me?.side === "white" ? "mode-picker__active" : "button--secondary"} onClick={() => void choose("white")}>Join White</button>
          <button type="button" className={me?.side === "black" ? "mode-picker__active" : "button--secondary"} onClick={() => void choose("black")}>Join Black</button>
        </div>
        {me ? (
          <button type="button" className={me.ready ? "button--secondary" : undefined} onClick={ready}>
            {me.ready ? "Retract start vote" : "Vote to start"}
          </button>
        ) : <p className="muted">Pick a team to vote for the game to start.</p>}
        {canStart ? <p className="success">Everyone is ready—starting…</p> : <p className="footnote muted">{game.democracy?.readyCount ?? 0}/{game.players.length} players ready</p>}
        {error ? <p className="error" role="alert">{error}</p> : null}
      </section>
      <section className="card democracy-lobby__settings">
        <div className="section-heading">
          <div>
            <h2>Game settings</h2>
            <p className="muted">
              {isCreator ? "Set the rules before the teams ready up." : "Only the game creator can change these before play begins."}
            </p>
          </div>
          {isCreator ? <span className="tag tag--quiet">Host</span> : null}
        </div>
        {isCreator ? (
          <>
            <TimeControlPicker value={timeControl} onChange={setTimeControl} />
            <fieldset className="vote-mode-picker">
              <legend>Vote rule</legend>
              {DEMOCRACY_VOTE_MODES.map((entry) => (
                <label
                  key={entry.mode}
                  className={`vote-mode-picker__option${voteMode === entry.mode ? " vote-mode-picker__option--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="lobby-vote-mode"
                    checked={voteMode === entry.mode}
                    onChange={() => setVoteMode(entry.mode)}
                  />
                  <span><strong>{entry.name}</strong><small>{entry.blurb}</small></span>
                </label>
              ))}
            </fieldset>
            <div className="row democracy-lobby__settings-actions">
              <button type="button" disabled={!settingsChanged || savingSettings} onClick={() => void saveSettings()}>
                {savingSettings ? "Saving…" : "Save settings"}
              </button>
              <p className="footnote muted">Saving reopens start votes so everyone agrees to the new setup.</p>
            </div>
          </>
        ) : (
          <p className="footnote"><strong>{voteRule?.name ?? "Lottery mode"}</strong> · {describeTimeControl(savedTimeControl)}</p>
        )}
      </section>
      <div className="team-grid">
        <Team
          gameId={game.id}
          side="white"
          players={white}
          me={me}
          online={online}
          onRemoveBot={(botUserId) => void handleRemoveBot(botUserId)}
          popcorn={isPopcorn(game.democracyVoteMode)}
          onReorder={(userIds) => void handleReorder("white", userIds)}
        />
        <Team
          gameId={game.id}
          side="black"
          players={black}
          me={me}
          online={online}
          onRemoveBot={(botUserId) => void handleRemoveBot(botUserId)}
          popcorn={isPopcorn(game.democracyVoteMode)}
          onReorder={(userIds) => void handleReorder("black", userIds)}
        />
      </div>
    </div>
  );
}
